import { createModel, RematchDispatch } from '@rematch/core';
import { message } from 'antd';
import produce from 'immer';
import { DEFAULT_PAGINATION } from '../../commons/constants/constants';
import { PageRequest, PaginationType } from '../../types/CommonTypes';
import { storeName as entityStore } from '../entities/cmsWebRotate.entity';
import { RootModel } from '../models';

export const storeName = 'webRotate';

export interface initialValuesType {
  ids: (number | string)[];
  pagination: PaginationType | null;
  criteria: { [key: string]: any };
  loading: boolean;
  editId: any;
  visible: boolean;
}

const initialState: initialValuesType = {
  ids: [],
  pagination: DEFAULT_PAGINATION,
  criteria: {},
  loading: false,
  editId: null,
  visible: false,
};

export const webRotate: any = createModel<RootModel>()({
  name: storeName,
  state: initialState,
  reducers: {
    setIds: produce((state, payload) => {
      state.ids = payload;
    }),
    setPagination: produce((state, payload) => {
      state.pagination = payload;
    }),
    setCriteria: produce((state, payload) => {
      state.criteria = payload;
    }),
    setLoading: produce((state, payload) => {
      state.loading = payload;
    }),
    setVisible: produce((state, payload) => {
      state.visible = payload;
    }),
    setEditId: produce((state, payload) => {
      state.editId = payload;
    }),
  },
  effects: (dispatch: RematchDispatch<typeof webRotate>) => ({
    async reloadList(payload, state) {
      const { criteria, pagination } = state[storeName];
      await this.filterAsync({ ...pagination, ...criteria });
    },
    async resetFilter(payload, state) {
      const { pagination } = state[storeName];
      await this.filterAsync({ ...pagination, page: 0 });
    },
    async pagingAsync(payload: PageRequest, state) {
      const { criteria = {}, pagination = {} } = state[storeName];
      await this.filterAsync({ ...pagination, ...criteria, ...payload });
    },
    async filterAsync(params = {}, state) {
      this.setLoading(true);
      try {
        const data = await dispatch[entityStore].load(params);
        if (data && data.content) {
          this.setIds(data.content.map((tour) => tour.id));
          this.setPagination((({ content, ...otherProps }) => otherProps)(data));
          this.setCriteria(params);
        }
      } catch (e) {
        console.error(e);
        message.error('Failed to filter web rotate');
      } finally {
        this.setLoading(false);
      }
    },
    async saveAsync(payload, state) {
      this.setLoading(true);
      let item;
      try {
        const params = { ...payload };
        item = await dispatch[entityStore].save(params);

        if (!params.id) {
          this.resetFilter();
        } else {
          this.reloadList();
        }

        message.success('Success to save web rotate');
      } catch (e) {
        console.error(e);
        message.error(`Fail to save web rotate`);
      } finally {
        this.setLoading(false);
      }
      return item;
    },
    async editAsync(payload, state) {
      this.setLoading(true);
      try {
        const item = await dispatch[entityStore].findById(payload);
        this.setEditId(item.id);
        this.setVisible(true);
      } catch (e) {
        console.error(e);
        message.error(`Failed to get ${payload}`);
      } finally {
        this.setLoading(false);
      }
    },
    async deleteAsync(payload, state) {
      this.setLoading(true);
      try {
        const isInUsed = await dispatch[entityStore].checkUsage(payload.code);
        if (isInUsed) {
          message.warning(`Failed to delete ${payload.name}, item is in used`);
          return;
        }

        await dispatch[entityStore].delete(payload.id);
        await this.reloadList();
        message.success(`Item ${payload.name} deleted`);
      } catch (e) {
        console.error(e);
        message.error(`Failed to delete ${payload.name}`);
      } finally {
        this.setLoading(false);
      }
    },
    async publishAsync(payload, state) {
      this.setLoading(true);
      try {
        await dispatch[entityStore].publish(payload);
        message.success(`Success to ${payload.published ? 'published' : 'unpublished'}`);
      } catch (e) {
        console.error(e);
        message.error(`Fail to ${payload.published ? 'published' : 'unpublished'}`);
      } finally {
        this.setLoading(false);
      }
    },
  }),
});

export default webRotate;
